import React, { useMemo } from 'react';
import { cn } from '../../utils/cn';
import { Button } from './Button';
import { useI18n } from '../../i18n/I18nContext';

export type PaginationChange = {
  limit: number;
  skip: number;
};

export function Pagination({
  total,
  limit,
  skip,
  onChange,
  limitOptions = [10, 20, 50, 100],
  className,
}: {
  total: number;
  limit: number;
  skip: number;
  onChange: (next: PaginationChange) => void;
  limitOptions?: number[];
  className?: string;
}) {
  const { t } = useI18n();
  const safeTotal = Math.max(0, total);
  const safeLimit = Math.max(1, limit);
  const safeSkip = Math.max(0, skip);

  const pageCount = Math.max(1, Math.ceil(safeTotal / safeLimit));
  const currentPage = Math.min(pageCount, Math.floor(safeSkip / safeLimit) + 1);

  const from = safeTotal === 0 ? 0 : (currentPage - 1) * safeLimit + 1;
  const to = Math.min(safeTotal, currentPage * safeLimit);

  const canPrev = currentPage > 1;
  const canNext = currentPage < pageCount;

  const pageNumbers = useMemo(() => {
    const set = new Set<number>();
    set.add(1);
    set.add(pageCount);
    set.add(currentPage);
    set.add(currentPage - 1);
    set.add(currentPage + 1);

    return Array.from(set)
      .filter((n) => n >= 1 && n <= pageCount)
      .sort((a, b) => a - b);
  }, [currentPage, pageCount]);

  function goToPage(page: number) {
    const p = Math.min(pageCount, Math.max(1, page));
    onChange({ limit: safeLimit, skip: (p - 1) * safeLimit });
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <div className="text-xs text-slate-500 dark:text-slate-400">
          {t('common.pagination.showing', { from, to, total: safeTotal })}
        </div>

        <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          {t('common.pagination.rowsPerPage')}
          <select
            className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            value={safeLimit}
            onChange={(e) => {
              const nextLimit = Math.max(1, Number(e.target.value) || safeLimit);
              const nextSkip = Math.floor(safeSkip / nextLimit) * nextLimit;
              onChange({ limit: nextLimit, skip: nextSkip });
            }}
          >
            {limitOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex items-center justify-between gap-2 sm:justify-end">
        <Button
          variant="secondary"
          size="sm"
          disabled={!canPrev}
          onClick={() => goToPage(currentPage - 1)}
        >
          {t('common.pagination.previous')}
        </Button>

        <div className="flex flex-wrap items-center justify-center gap-1">
          {pageNumbers.map((p, idx) => {
            const prev = pageNumbers[idx - 1];
            const showEllipsis = idx > 0 && prev !== undefined && p - prev > 1;
            return (
              <React.Fragment key={p}>
                {showEllipsis ? (
                  <span className="px-2 text-xs text-slate-400">…</span>
                ) : null}
                <button
                  type="button"
                  className={cn(
                    'min-w-[2.25rem] rounded-lg px-2 py-1.5 text-sm transition',
                    p === currentPage
                      ? 'bg-red-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                  )}
                  onClick={() => goToPage(p)}
                  aria-current={p === currentPage ? 'page' : undefined}
                >
                  {p}
                </button>
              </React.Fragment>
            );
          })}
        </div>

        <Button
          variant="secondary"
          size="sm"
          disabled={!canNext}
          onClick={() => goToPage(currentPage + 1)}
        >
          {t('common.pagination.next')}
        </Button>
      </div>
    </div>
  );
}
